import { Button, Box, IconButton, Skeleton, Tooltip, CircularProgress } from '@mui/material'
import { Add } from '@mui/icons-material'
import Lottie from 'react-lottie'

import CriterionCard from '../Rubric/CriterionCard'
import ClickAndEditText from '../ClickAndEditText'
import RubricPointsChip from '../Rubric/RubricPointsChip'

import { ReactComponent as EditIcon } from '@/assets/icons/edit-05.svg'
import { ReactComponent as TrashIcon } from '@/assets/icons/trash-blue.svg'
import { ReactComponent as ShareIcon } from '@/assets/icons/share-blue.svg'
import { CopyIcon } from '@/assets/icons/copy-icon'
import animationData from '@/assets/app_art/magin_hand.json'
import useUtils from '@/hooks/useUtilities';

const defaultOptions = {
  loop: true,
  autoplay: true,
  animationData: animationData,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
}

function Editor({
  rubric,
  editing = true,
  isOwner = false,
  handleEditRubric = () => {},
  onUpdateRubric = () => {},
  copyRubric = () => {},
  handleDelete = () => {},
  loading = false,
  copyingRubric = false,
}) {
  const {showSnackbar} = useUtils() 
  const handleAddCriterion = () => {
    const newCriterion = {
      criterion_id: `criterion_${rubric.criteria.length + 1}`,
      description: '',
      title: '',
      score_definitions: [
        {
          score_id: 'score_0',
          level: rubric?.start || 0,
          definition: '',
        },
        {
          score_id: 'score_1',
          level: rubric?.start + 1 || 1,
          definition: '',
        },
      ],
    }

    onUpdateRubric({
      criteria: [...rubric.criteria, newCriterion],
    })
  }

  const handleUpdateCriterion = (id, criterion) => {
    const updatedCriteria = [...rubric.criteria]
    const index = updatedCriteria.findIndex(c => c.criterion_id === id)

    updatedCriteria[index] = criterion
    onUpdateRubric({
      criteria: updatedCriteria,
    })
  }

  const handleUpdateScoreTitles = (index, title) => {
    const updatedTitles = {
      ...rubric.score_titles,
    }

    updatedTitles[index] = title
    onUpdateRubric({
      score_titles: updatedTitles,
    })
  }

  const handleDeleteCriterion = criterion_id => {
    if (!window.confirm('Are you sure you want to delete this criterion?')) return
    const updatedCriteria = rubric.criteria.filter(c => c.criterion_id !== criterion_id)

    onUpdateRubric({
      criteria: updatedCriteria,
    })
  }

  const handleNameChange = value => {
    onUpdateRubric({
      name: value,
    })
  }

  const handleDescriptionChange = value => {
    onUpdateRubric({
      description: value,
    })
  }

  const handleCopyRubricLink = rubricId => {
    const link = `${window.location.origin}/rubric/${rubricId}`

    navigator.clipboard
      .writeText(link)
      .then(() => {
        showSnackbar('Rubric link copied to clipboard!', 'success')
      })
      .catch(err => {
        console.error('Failed to copy rubric link: ', err)
      })
  }

  return (
    <Box
      sx={{
        width: '100%',
        position: 'relative',
      }}
    >
      {loading ? (
        <Box
          sx={{
            position: 'absolute',
            width: '100%',
            height: '100%',
          }}
        >
          <Lottie height={500} options={defaultOptions} speed={2} width={500} />
        </Box>
      ) : null}
      <Box sx={{ mb: '22px' }}>
        <Box
          sx={{
            display: 'flex',
            gap: '16px',
            overflow: 'hidden',
            width: '100%',
            alignItems: 'center',
          }}
        >
          <ClickAndEditText
            InputBaseProps={{
              fullWidth: true,
            }}
            editable={editing}
            loading={loading}
            placeholder="Rubric Name"
            sx={{
              fontSize: 24,
              fontWeight: 600,
            }}
            value={rubric?.name}
            onChange={handleNameChange}
          />

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            {!!rubric?.total_points && <RubricPointsChip title={`${rubric?.total_points} points`} />}

            {!editing && (
              <Button
                color="primary"
                startIcon={<EditIcon />}
                sx={{
                  color: 'primary.dark',
                  width: 'fit-content',
                  whiteSpace: 'nowrap',
                }}
                onClick={handleEditRubric}
              >
                {isOwner ? 'Edit' : 'Copy & Edit'}
              </Button>
            )}
            <Tooltip arrow title="Share rubric">
              <IconButton onClick={() => handleCopyRubricLink(rubric.id)}>
                <ShareIcon />
              </IconButton>
            </Tooltip>
            {isOwner ? (
              <Tooltip arrow title={`${copyingRubric ? 'Copying' : 'Copy'} rubric`}>
                <IconButton onClick={() => copyRubric(rubric.id)}>
                  {copyingRubric ? <CircularProgress size={20} /> : <CopyIcon />}
                </IconButton>
              </Tooltip>
            ) : null}
            {isOwner ? (
              <Tooltip arrow title="Delete rubric">
                <IconButton onClick={handleDelete}>
                  <TrashIcon />
                </IconButton>
              </Tooltip>
            ) : null}
          </Box>
        </Box>
        <ClickAndEditText
          InputBaseProps={{
            multiline: true,
          }}
          editable={editing}
          loading={loading}
          placeholder="What is the rubric about?"
          sx={{
            fontSize: 14,
            fontWeight: 400,
            color: '#475467',
          }}
          value={rubric?.description}
          onChange={handleDescriptionChange}
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
          width: '100%',
          mb: '16px',
        }}
      >
        {loading ? (
          <>
            {[1, 2, 3].map(i => (
              <Skeleton key={i} height={200} variant="rectangular" width="100%" />
            ))}
          </>
        ) : (
          <>
            {rubric?.criteria
              ? rubric?.criteria?.map((criterion, index) => (
                  <CriterionCard
                    key={criterion.criterion_id}
                    defaultExpanded
                    criterion={criterion}
                    deleteCriterion={handleDeleteCriterion}
                    editing={editing}
                    score_titles={rubric?.score_titles}
                    sortDesc={rubric?.sort === 'desc'}
                    startFrom={rubric?.start}
                    onUpdateCriterion={(id, c) => handleUpdateCriterion(id, c)}
                    onUpdateScoreTitles={handleUpdateScoreTitles}
                  />
                ))
              : null}
          </>
        )}
      </Box>

      {editing ? (
        <Box
          sx={{
            width: '44px',
            height: '44px',
            borderRadius: '16px',
            border: '1px solid #D0D5DD',
            boxShadow: '0px 1px 16px 0px rgba(16, 24, 40, 0.10)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            '&:hover': {
              bgcolor: '#F9FAFB',
            },
            cursor: 'pointer',
          }}
          onClick={handleAddCriterion}
        >
          <Add color="secondary" />
        </Box>
      ) : null}
    </Box>
  )
}

export default Editor
